import { BadgeProps } from "ui/components/Badge/types";
import { OfferRentSearchFormValues, OfferSaleSearchFormValues } from "./types";

export const initialOfferSaleSearchForm: OfferSaleSearchFormValues = {
  search: null,
  offerSaleStatusId: null,
};


export const initialOfferRentSearchForm: OfferRentSearchFormValues = {
  search: null,
};

export const getStatusBadgeVariant = (color: string): BadgeProps["variant"] => {
  switch (color) {
    case "blue":
      return "info";
    case "green":
      return "success";
    case "red":
      return "danger";
    case "yellow":
      return "warning";
    default:
      return "info";
  }
};

export const getStatusCircleColor = (color: string): string => {
  switch (color) {
    case "blue":
      return "bg-blue-500";
    case "green":
      return "bg-green-500";
    case "red":
      return "bg-red-500";
    case "yellow":
      return "bg-yellow-500";
    default:
      return "bg-blue-500";
  }
};

/**
 * Mapiranje statusa ponude (sale i rent) na badge variant temeljem semantike pipeline-a.
 * Konzistentno s OffersCard u Overview-u: poslano=plavo, prihvaćeno=žuto, plaćeno=zeleno.
 * Hardkodirano po status.id da osigura jednoznačnu boju neovisno o postavkama u bazi.
 */
export const getOfferStatusBadgeVariant = (statusId: string | null | undefined): BadgeProps["variant"] => {
  switch (statusId) {
    case "draft":
      return "secondary";
    case "created":
      return "violet";
    case "sent":
      return "info";
    case "accepted":
      return "warning";
    case "paid":
      return "success";
    case "cancelled":
      return "danger";
    default:
      return "secondary";
  }
};

/**
 * Tailwind klasa za bar/circle indikator po statusu — koristi se u progres trakama.
 * Tonovi prate logiku pipeline-a (svjetlije = pasivnije, intenzivnije = bliže uspjehu).
 */
export const getOfferStatusBarColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "draft":
      return "bg-slate-300";
    case "created":
      return "bg-slate-300";
    case "sent":
      return "bg-blue-400";
    case "accepted":
      return "bg-yellow-400";
    case "paid":
      return "bg-green-500";
    case "cancelled":
      return "bg-red-400";
    default:
      return "bg-slate-300";
  }
};

/**
 * Prozirni gradient ispune (jak na lijevoj strani -> blijedi udesno) po statusu.
 * Koristi se za trake kod kojih podaci stoje UNUTAR bara — mekana ispuna
 * zadržava čitljivost tamnog teksta preko nje. Pune klase za Tailwind JIT.
 */
export const getOfferStatusBarFill = (statusId: string | null | undefined): string => {
  switch (statusId) {
    // Gradient okrenut: jak na kraju (desno, uz vertikalni border) -> blijedi prema početku.
    // Omjeri opacitea i boje usklađeni s overview grafom + 2px border na kraju (border-r).
    case "sent":
      return "bg-gradient-to-l from-sky-500/25 to-sky-500/[0.02] border-r-2 border-sky-500 group-hover/row:from-sky-500/40";
    case "accepted":
      return "bg-gradient-to-l from-yellow-300/70 to-yellow-300/10 border-r-2 border-yellow-400 group-hover/row:from-yellow-300/85";
    case "paid":
      return "bg-gradient-to-l from-emerald-500/30 to-emerald-500/[0.02] border-r-2 border-emerald-500 group-hover/row:from-emerald-500/45";
    case "cancelled":
      return "bg-gradient-to-l from-rose-500/25 to-rose-500/[0.02] border-r-2 border-rose-500 group-hover/row:from-rose-500/40";
    case "created":
      return "bg-gradient-to-l from-violet-400/30 to-violet-400/[0.02] border-r-2 border-violet-400 group-hover/row:from-violet-400/45";
    case "draft":
    default:
      return "bg-gradient-to-l from-slate-400/30 to-slate-400/[0.02] border-r-2 border-slate-400 group-hover/row:from-slate-400/45";
  }
};

/**
 * Boja iznosa po statusu — selektivno: samo terminalni ishodi (plaćeno = zeleno, otkazano = crveno),
 * ostale faze ostaju neutralne. Pune klase za Tailwind JIT.
 */
export const getOfferStatusAmountColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "paid":
      return "text-emerald-600";
    case "cancelled":
      return "text-rose-600";
    default:
      return "text-slate-800";
  }
};

/**
 * Boja ringa (hover) po statusu — usklađena s bojom ispune. Pune klase za Tailwind JIT.
 */
export const getOfferStatusRingColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "sent":
      return "hover:ring-sky-400";
    case "accepted":
      return "hover:ring-yellow-400";
    case "paid":
      return "hover:ring-emerald-400";
    case "cancelled":
      return "hover:ring-rose-400";
    case "created":
      return "hover:ring-violet-400";
    case "draft":
    default:
      return "hover:ring-slate-400";
  }
};
